import React, { useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Main, LeftContet, RightContet, ScrollY, Row, InfoWrapper } from './components'
import { Organizationinfo } from '@/types'
import Modal, { confirm } from '@/components/Modal'
import InfoCard from './components/infoCard'
import MyProducts from './components/products'
import Marketplace from './components/marketplace'
import { AuthContext } from '@/layout/Auth'
import {
  getOrgsApplications,
  getPurchasedApplications,
  setDefaultOrg,
  subscribeApplication,
  getUerPermissions,
  getOrgInfo,
  getUserInfo,
} from '@/api'
import { useQuery, useMutation } from '@tanstack/react-query'
import { ApplicationInfo } from '@/types'
import { encodeUrl } from '@/utils'
import { usePermission } from '@/hooks'
import { Stack, Box } from '@mui/material'
import Text from '@/components/Text'
import { ImageIcon } from '@/icon'
import assistantCode from '@/asset/img/assistant_code.webp'
import { Message } from '@cx/ui'

export type MarketSortType = 'latest' | 'hottest'

const FormatTime = ({ app }: { app: ApplicationInfo }) => {
  const [remainingTime, setRemainingTime] = useState(app.certification_duration)
  const formatTime = (time: any) => {
    const days = Math.floor(time / (24 * 60 * 60))
    const hours = Math.floor((time % (24 * 60 * 60)) / 3600)
    const minutes = Math.floor((time % 3600) / 60)
    const seconds = Math.floor(time % 60)

    const formatNumber = (number: any) => (number < 10 ? `0${number}` : number)

    const daysString = days > 0 ? `${days} 天 ` : ''
    const hoursString = hours > 0 || days > 0 ? `${formatNumber(hours)} 小时 ` : ''
    const minutesString = minutes > 0 || hours > 0 || days > 0 ? `${formatNumber(minutes)} 分 ` : ''
    const secondsString = `${formatNumber(seconds)} 秒`

    return `${daysString}${hoursString}${minutesString}${secondsString}`
  }
  useEffect(() => {
    const intervalId = setInterval(() => {
      setRemainingTime((prevTime) => Math.max(0, prevTime! - 1))
    }, 1000)

    return () => clearInterval(intervalId)
  }, [])

  return (
    <Box sx={{ mt: 1 }}>
      <Box component='span' sx={{ fontWeight: 600 }}>
        {app.name}，
      </Box>
      实名认证倒计时剩余{' '}
      <Box component='span' sx={{ color: 'info.main' }}>
        {formatTime(remainingTime)}
      </Box>
    </Box>
  )
}

const AuthPrompt: React.FC<{ apps: ApplicationInfo[] }> = ({ apps }) => {
  return (
    <Box sx={{ fontSize: 14 }}>
      为响应《网络安全法》并保障您的权益，以下应用需要您进行实名认证方可使用。
      请注意，若未进行实名认证，将无法正常使用以下应用：
      {apps.map((a) => {
        return <FormatTime key={a.name} app={a}></FormatTime>
      })}
    </Box>
  )
}

const Workbench: React.FC<unknown> = () => {
  const [searchParams] = useSearchParams()
  const assistant = searchParams?.get('assistant')
  const hasPrompt = useRef<boolean>(false)
  const { authInfo, updateAuthInfo } = useContext(AuthContext)
  const perms = usePermission()
  const navigate = useNavigate()
  const org_id = authInfo?.orgInfo?.id
  const [sort, setSort] = useState<MarketSortType>('hottest')
  const [showHelp, setShowHelp] = useState<boolean>(assistant === 'wechat')

  const { data: orgInfo } = useQuery(
    ['getOrgInfo', org_id],
    // @ts-ignore
    () => getOrgInfo(org_id),
    {
      enabled: !!org_id,
    },
  )

  const { data: userInfo } = useQuery(['getUserInfo'], getUserInfo)

  const {
    data: purchased,
    isLoading,
    refetch: refetchPurchased,
  } = useQuery(
    ['getPurchasedApplications', org_id],
    // @ts-ignore
    () => getPurchasedApplications(org_id),
    {
      enabled: !!org_id && !!userInfo,
      onSuccess(res) {
        const appNames = res?.apps?.filter(
          (item) =>
            !!item.certification_date && (item.certification_duration || 0) > 0 && item.require_real_name_cert === 1,
        )

        if (appNames?.length && !hasPrompt.current && userInfo?.is_certified !== 1) {
          confirm({
            title: '提示',
            width: 560,
            content: <AuthPrompt apps={appNames} />,
            okText: '去认证',
            cancelText: '暂不认证',
            closeAfterClose: true,
            onConfirm: () => navigate('/console/personal'),
            onClose: () => {},
          })
          hasPrompt.current = true
        }
      },
    },
  )

  const {
    data: market,
    isLoading: isMarketLoading,
    refetch: refetchMarket,
  } = useQuery(
    ['getOrgsApplications', org_id, sort],
    () =>
      getOrgsApplications({
        // @ts-ignore
        org_id,
      }),
    {
      enabled: !!org_id && perms?.includes('admin'),
    },
  )
  const unpurchasedApps = useMemo(() => {
    return market?.data?.filter((item) => !purchased?.apps?.find((purchase) => purchase.id === item.id))
  }, [market, purchased?.apps])
  const defaultOrg = useMutation(setDefaultOrg, {
    onSuccess: () => {
      Message.success('切换空间成功')
    },
  })
  const subscribe = useMutation(subscribeApplication, {
    onSuccess: (_, params) => {
      Message.success('开通成功')
      refetchPurchased()
      refetchMarket()
      enterApp(market?.data.find((item) => item.id === params.id)!)
    },
    onError: (error) => {
      if (error == 4127) {
        confirm({
          title: '提示',
          content: '开通此应用需要实名认证',
          hideClose: true,
          okText: '去认证',
          closeAfterClose: true,
          onConfirm: () => navigate('/console/personal'),
          onClose: () => {},
        })
      } else {
        if (typeof error !== 'string') {
          Message.error('开通失败，请提交工单')
        }
      }
    },
  })

  useQuery(
    ['getUerPermissions', org_id],
    // @ts-ignore
    () => org_id && getUerPermissions(org_id),
    {
      refetchOnMount: true,
      enabled: !!org_id,
      onSuccess: (res) => {
        // @ts-ignore
        updateAuthInfo({ permissions: res.data })
      },
    },
  )

  const onChangeOrg = useCallback(
    (newOrg: Organizationinfo) => {
      defaultOrg.mutate({ ID: newOrg.id })
      updateAuthInfo({ orgInfo: newOrg })
    },
    [defaultOrg, updateAuthInfo],
  )

  const enterApp = (app: ApplicationInfo) => {
    if (
      app.certification_date &&
      app.certification_duration === 0 &&
      userInfo?.is_certified !== 1 &&
      app.require_real_name_cert === 1
    ) {
      confirm({
        title: '提示',
        content: '为响应《网络安全法》并保障您的权益，该应用需要您进行实名认证方可使用。',
        okText: '去认证',
        cancelText: '暂不认证',
        closeAfterClose: true,
        onConfirm: () => navigate('/console/personal'),
        onClose: () => {},
      })
    } else {
      const matchKey = app.url_prefix.split('.')[0].match(/^https?:\/\/(.+)/)
      console.log(app)
      // window.open(`/console/app/${matchKey ? matchKey[1] : ''}${encodeUrl(app.url_prefix)}`)
    }
  }

  const purchaseApp = useCallback(
    (app: ApplicationInfo) => {
      // @ts-ignore
      subscribe.mutate({ id: app.id, org_id: org_id, package_id: 12 })
    },
    [org_id, subscribe],
  )

  const handleCloseHelp = useCallback(() => {
    setShowHelp(false)
    navigate('/console/workbench')
  }, [navigate])

  return (
    <Main sx={{ overflow: 'auto', overflowY: 'overlay', pb: '14px' }} style={{ overflow: 'overlay' }}>
      <LeftContet>
        <InfoCard info={orgInfo!} onChangeOrg={onChangeOrg} />
      </LeftContet>
      <RightContet>
        <ScrollY sx={{ background: '#fff', borderRadius: '4px', overflow: 'auto' }}>
          <MyProducts
            products={purchased?.apps!}
            loading={isLoading}
            enterApp={enterApp}
            // @ts-ignore
            org_id={org_id}
          />
          {perms?.includes('admin') && (unpurchasedApps?.length || 0) > 0 && (
            <Marketplace
              sort={sort}
              data={unpurchasedApps!}
              loading={isMarketLoading}
              enterApp={enterApp}
              purchaseApp={purchaseApp}
              onSortChange={setSort}
            />
          )}
        </ScrollY>
      </RightContet>
      <Modal open={showHelp} onClose={handleCloseHelp} maskClosable={true}>
        <InfoWrapper sx={{ background: '#fff', padding: '16px' }}>
          <Stack
            sx={{
              alignItems: 'center',
            }}
          >
            <Row sx={{ justifyContent: 'center' }}>
              <Text weight='bold'>请微信联系百川万能小助手</Text>
              {/* <ClearIcon sx={{ cursor: "pointer" }} onClick={handleCloseHelp} /> */}
            </Row>
            <ImageIcon src={assistantCode} sx={{ width: '160px', margin: '24px' }} />
          </Stack>
        </InfoWrapper>
      </Modal>
    </Main>
  )
}

export default Workbench
